import { type CustomCellEditorProps } from "ag-grid-react";
import type { BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import { useCallback, useRef } from "react";

import { TempFileTypes } from "types/enum";

import { BTUpload } from "commonComponents/btWrappers/BTUpload/BTUpload";
import { type IPendingFileUpload } from "commonComponents/entity/customField/utils/customFieldTypes/FileCustomFieldHandler";

import { type FileCustomFieldDefinition } from "handlers";

interface IFileCellEditorParams {
    definition?: FileCustomFieldDefinition;
}

/**
 * Invisible cell editor that auto-triggers the file picker.
 * Used for File custom fields in the grid — clicking an empty cell
 * opens the native file picker dialog immediately.
 */
export const FileCellEditor = ({
    onValueChange,
    stopEditing,
    definition,
}: CustomCellEditorProps<unknown, string | null> & IFileCellEditorParams) => {
    const hasTriggeredRef = useRef(false);

    const containerRefCallback = useCallback(
        (node: HTMLDivElement | null) => {
            if (!node || hasTriggeredRef.current) return;
            hasTriggeredRef.current = true;

            const fileInput = node.querySelector<HTMLInputElement>('input[type="file"]');
            if (fileInput) {
                // Exit edit mode when the user cancels the file dialog
                fileInput.addEventListener("cancel", () => stopEditing(), { once: true });

                // Exit edit mode when the selected file doesn't match allowed types.
                // Ant Upload's beforeUpload silently rejects the file without calling
                // our setFieldValue callback, so the editor would stay open.
                const allowedTypes = definition?.allowedFileTypes ?? ["*"];
                if (allowedTypes.length > 0 && allowedTypes[0] !== "*") {
                    fileInput.addEventListener("change", () => {
                        const file = fileInput.files?.[0];
                        if (!file) return;
                        const ext = file.name.split(".").pop()?.toLowerCase();
                        if (ext && !allowedTypes.some((t) => t.toLowerCase() === ext)) {
                            stopEditing();
                        }
                    });
                }
            }

            // BTUpload doesn't expose a prop to auto-open the native file picker,
            // so we programmatically click its hidden upload button to trigger the
            // file dialog immediately when the cell editor mounts.
            const uploadButton = node.querySelector<HTMLElement>('[data-testid="upload"]');
            if (uploadButton) {
                uploadButton.click();
            } else {
                stopEditing();
            }
        },
        [stopEditing, definition],
    );

    const handleUploadComplete = useCallback(
        (_field: string, fileUpload: BTServiceFileUpload | BTServiceFileUpload[] | undefined) => {
            if (!fileUpload || Array.isArray(fileUpload)) {
                stopEditing();
                return;
            }

            const value = JSON.stringify({
                tempId: fileUpload.tempId,
                ext: fileUpload.ext,
                fileName: fileUpload.fileName,
            } satisfies IPendingFileUpload);
            onValueChange?.(value);
            stopEditing();
        },
        [onValueChange, stopEditing],
    );

    return (
        <div
            ref={containerRefCallback}
            style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
        >
            <BTUpload
                id="fileCustomFieldCellEditor"
                data-testid="fileCustomFieldCellUpload"
                tempFileType={TempFileTypes.CustomFieldFiles}
                allowedFileTypes={definition?.allowedFileTypes ?? ["*"]}
                setFieldValue={handleUploadComplete}
                value={undefined}
                useBdsButtonStyle
                buttonType="secondary"
                uploadButtonText="Upload"
            />
        </div>
    );
};
